import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import Captcha from './components/Captcha';
import supabase from './supabase/supabase';

const { FiCheck, FiAlertCircle, FiLoader, FiGift, FiInfo } = FiIcons;

const API_CONFIG = {
  // Use env variable with fallback so the app won't crash if it's not set
  redirectUrl: import.meta.env.VITE_REDIRECT_URL || 'https://sharemyimage.com/signup/?smiappsumosignup'
};

function App({ userAccountId }) {
  const [code, setCode] = useState('');
  const [userFields, setUserFields] = useState({ name: '', email: '', country: '', purchaseDate: '' });
  const [recordId, setRecordId] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showUserFields, setShowUserFields] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [messages, setMessages] = useState({
    utilized: false,
    duplicateEmail: false,
    planDetails: '',
    deliveryEstimate: '',
    error: false
  });

  useEffect(() => {
    if (code) {
      resetFormState();
    }
  }, [code]);

  const resetFormState = () => {
    setMessages({
      utilized: false,
      duplicateEmail: false,
      planDetails: '',
      deliveryEstimate: '',
      error: false
    });
    setShowUserFields(false);
  };

  const resetForm = () => {
    setCode('');
    setUserFields({ name: '', email: '', country: '', purchaseDate: '' });
    resetFormState();
    setRecordId(null);
    setIsCaptchaVerified(false);
    setIsSubmitted(false);
  };

  const handleCaptchaVerify = (isVerified) => {
    setIsCaptchaVerified(isVerified);
  };

  const sanitizeInput = (input) => {
    return input.replace(/[^a-zA-Z0-9-_]/g, '');
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const getDeliveryEstimate = async () => {
    if (!isCaptchaVerified) {
      setMessages(prev => ({ ...prev, deliveryEstimate: 'Security verification required.', error: true }));
      return;
    }

    const sanitizedCode = sanitizeInput(code);

    if (!sanitizedCode.trim() || sanitizedCode !== code) {
      setMessages(prev => ({ ...prev, deliveryEstimate: 'Please enter a valid alphanumeric code.', error: true }));
      return;
    }

    setIsLoading(true);

    try {
      // Use secure RPC to prevent database scraping
      const { data: records, error } = await supabase
        .rpc('verify_redemption_code', { lookup_code: sanitizedCode });

      if (error) throw error;

      if (records && records.length > 0) {
        const record = records[0];
        setRecordId(record.id);

        const { name, email, country, purchase_date } = record;

        // If it has a name or email, it means someone already redeemed it
        if (name || email || country || purchase_date) {
          setMessages(prev => ({ ...prev, utilized: true, error: false }));
          setShowUserFields(false);
        } else {
          setShowUserFields(true);
          setMessages(prev => ({ ...prev, utilized: false, planDetails: record.plan, error: false }));
        }
      } else {
        setMessages(prev => ({ ...prev, deliveryEstimate: 'Invalid code provided.', error: true }));
        setShowUserFields(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setMessages(prev => ({ ...prev, deliveryEstimate: 'Error communicating with server.', error: true }));
    } finally {
      setIsLoading(false);
    }
  };

  const submitUserDetails = async () => {
    if (isSubmitted) return;

    if (!code.trim() || !userFields.name.trim() || !userFields.email.trim() || !userFields.country.trim() || !userFields.purchaseDate.trim()) {
      alert("Please fill in all fields.");
      return;
    }

    if (!validateEmail(userFields.email)) {
      alert("Please enter a valid email address.");
      return;
    }

    if (!recordId) {
      alert("Please verify your code first.");
      return;
    }

    setIsLoading(true);

    try {
      // Use secure RPC to update and check for constraints atomically
      const { error: updateError } = await supabase
        .rpc('redeem_code', {
          p_record_id: recordId,
          p_name: userFields.name,
          p_email: userFields.email,
          p_country: userFields.country,
          p_purchase_date: userFields.purchaseDate,
          p_user_account_id: userAccountId || null
        });

      if (updateError) {
        if (updateError.message.includes('Email already in use')) {
          setMessages(prev => ({ ...prev, duplicateEmail: true }));
          setIsLoading(false);
          return;
        }
        if (updateError.message.includes('already been redeemed')) {
          setMessages(prev => ({ ...prev, utilized: true }));
          setIsLoading(false);
          return;
        }
        throw updateError;
      }

      setMessages(prev => ({ ...prev, duplicateEmail: false }));

      // Show success screen and redirect after 3.5 seconds
      setIsSubmitted(true);
      setTimeout(() => {
        window.location.replace(API_CONFIG.redirectUrl);
      }, 3500);

    } catch (error) {
      console.error("Error updating details:", error);
      alert("Error updating details. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setUserFields(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-100 to-green-100 rounded-full translate-y-12 -translate-x-12 opacity-50"></div>

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="flex justify-center mb-8"
        >
          <img src="https://cdn.sharemyimage.com/content/images/system/logo_1757753822551_725932.jpeg" alt="Logo"  width="50%" className="w-50 h-50 object-contain" />
        </motion.div>

        <AnimatePresence mode="wait">
          {isSubmitted ? (
            <motion.div
              key="success-state"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="text-center py-6"
            >
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 shadow-inner">
                <SafeIcon icon={FiCheck} className="text-4xl text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Thanks for Submission!</h2>
              <p className="text-gray-600 mb-6">Your code has been successfully redeemed.</p>

              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-4 mb-8 text-left shadow-sm">
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 text-lg flex-shrink-0" />
                  <p className="text-sm text-blue-800">
                    <strong className="block mb-1">Important Reminder:</strong>
                    Please sign up using <strong>{userFields.email}</strong>. The deal will only apply if the signup email matches exactly.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3 text-sm font-medium text-gray-600 bg-gray-50 py-3 px-4 rounded-full w-max mx-auto border border-gray-100">
                <SafeIcon icon={FiLoader} className="animate-spin text-orange-500 text-lg" />
                Redirecting to signup...
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="form-state"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
                  <SafeIcon icon={FiGift} className="text-orange-500" />
                  Code Redemption
                </h1>
                <p className="text-gray-600 text-sm">Enter your code to redeem your plan</p>
              </div>

              <Captcha onVerify={handleCaptchaVerify} isVerified={isCaptchaVerified} />

              <AnimatePresence>
                {isCaptchaVerified && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 mb-6"
                  >
                    <div className="relative">
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                        placeholder="Enter Code (Alphanumeric only)"
                        maxLength="20"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-all duration-300 text-center font-mono text-lg tracking-wider uppercase"
                      />
                    </div>

                    {!showUserFields && (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={getDeliveryEstimate}
                        disabled={isLoading || !code.trim()}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <><SafeIcon icon={FiLoader} className="animate-spin" /> Verifying...</>
                        ) : (
                          <><SafeIcon icon={FiCheck} /> Verify Your Code</>
                        )}
                      </motion.button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {messages.planDetails && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6"
                  >
                    <div className="flex items-center gap-2 text-green-700">
                      <SafeIcon icon={FiCheck} className="text-green-500" />
                      <span className="font-semibold">Your Plan Details:</span>
                    </div>
                    <p className="text-green-800 mt-1">{messages.planDetails}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {showUserFields && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 mb-6"
                  >
                    <div className="grid gap-4">
                      <input
                        type="text"
                        value={userFields.name}
                        maxLength="50"
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Full Name"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-all duration-300"
                        required
                      />
                      <input
                        type="email"
                        value={userFields.email}
                        maxLength="80"
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Email Address"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-all duration-300"
                        required
                      />
                      <input
                        type="text"
                        value={userFields.country}
                        maxLength="50"
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        placeholder="Country"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-all duration-300"
                        required
                      />
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date</label>
                        <input
                          type="date"
                          value={userFields.purchaseDate}
                          max={new Date().toISOString().split("T")[0]}
                          onChange={(e) => handleInputChange('purchaseDate', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-all duration-300"
                          required
                        />
                      </div>
                    </div>

                    {/* Email Matching Notification */}
                    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-4 my-4">
                      <div className="flex items-start gap-3">
                        <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5 text-lg flex-shrink-0" />
                        <p className="text-sm text-blue-800">
                          <strong className="block mb-1">Important Notice:</strong>
                          The email address used for code redemption and signup must be exactly the same. If you sign up with a different email address, this deal will not be applicable.
                        </p>
                      </div>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={submitUserDetails}
                      disabled={isLoading || isSubmitted}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <><SafeIcon icon={FiLoader} className="animate-spin" /> Submitting...</>
                      ) : (
                        <><SafeIcon icon={FiCheck} /> Securely Submit Details</>
                      )}
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {messages.utilized && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4 text-center mt-4"
                  >
                    <div className="flex items-center justify-center gap-2 text-red-600">
                      <SafeIcon icon={FiAlertCircle} />
                      <span className="font-semibold">Code Already Utilized</span>
                    </div>
                    <p className="text-red-700 text-sm mt-1">This code has already been redeemed.</p>
                  </motion.div>
                )}

                {messages.duplicateEmail && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4 text-center mt-4"
                  >
                    <div className="flex items-center justify-center gap-2 text-red-600">
                      <SafeIcon icon={FiAlertCircle} />
                      <span className="font-semibold">Email Already in Use</span>
                    </div>
                    <p className="text-red-700 text-sm mt-1">This email is already associated with a redeemed code.</p>
                  </motion.div>
                )}

                {messages.error && messages.deliveryEstimate && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4 text-center mt-4"
                  >
                    <div className="flex items-center justify-center gap-2 text-red-600">
                      <SafeIcon icon={FiAlertCircle} />
                      <span className="font-semibold">Error</span>
                    </div>
                    <p className="text-red-700 text-sm mt-1">{messages.deliveryEstimate}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export default App;